import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  Filter,
  Briefcase,
  Calendar,
  DollarSign,
  Clock,
  Loader2,
  Upload,
  File,
  X,
  UserPlus,
  CheckSquare,
  Archive,
  Trash2,
  MoreHorizontal
} from 'lucide-react';

const JobsPage = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Bulk selection state
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [bulkActionDialogOpen, setBulkActionDialogOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkStatus, setBulkStatus] = useState('');
  const [processingBulk, setProcessingBulk] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  // New customer dialog
  const [newCustomerDialogOpen, setNewCustomerDialogOpen] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: '', company: '', email: '', phone: '' });
  const [creatingCustomer, setCreatingCustomer] = useState(false);

  const [newJob, setNewJob] = useState({
    title: '',
    job_type: '',
    customer_id: '',
    description: '',
    budget: '',
    due_date: '',
    objectives: '',
    deliverables: '',
    notes: ''
  });

  const fetchData = async () => {
    try {
      const [jobsRes, customersRes, typesRes] = await Promise.all([
        axios.get(`${API}/jobs`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/customers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/job-types`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setJobs(jobsRes.data);
      setCustomers(customersRes.data);
      setJobTypes(typesRes.data.job_types);
    } catch (err) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const handleCreateCustomer = async () => {
    if (!newCustomer.name) {
      toast.error('Please enter customer name');
      return;
    }
    setCreatingCustomer(true);
    try {
      const response = await axios.post(`${API}/customers`, newCustomer, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Customer created!');
      setNewCustomerDialogOpen(false);
      setNewJob({ ...newJob, customer_id: response.data.id });
      setNewCustomer({ name: '', company: '', email: '', phone: '' });
      fetchData();
    } catch (err) {
      toast.error('Failed to create customer');
    } finally {
      setCreatingCustomer(false);
    }
  };

  const handleCreateJob = async () => {
    if (!newJob.title || !newJob.job_type || !newJob.customer_id) {
      toast.error('Please fill in required fields (Title, Type, Customer)');
      return;
    }

    setCreating(true);
    try {
      // Combine all text fields into description
      let fullDescription = newJob.description || '';
      if (newJob.objectives) {
        fullDescription += `\n\n**Objectives:**\n${newJob.objectives}`;
      }
      if (newJob.deliverables) {
        fullDescription += `\n\n**Deliverables:**\n${newJob.deliverables}`;
      }
      if (newJob.notes) {
        fullDescription += `\n\n**Notes:**\n${newJob.notes}`;
      }

      const payload = {
        title: newJob.title,
        job_type: newJob.job_type,
        customer_id: newJob.customer_id,
        description: fullDescription.trim(),
        budget: newJob.budget ? parseFloat(newJob.budget) : null,
        due_date: newJob.due_date || null
      };
      
      const response = await axios.post(`${API}/jobs`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const createdJobId = response.data.id;
      const jobNumber = response.data.job_number;

      // Upload files if any
      if (filesToUpload.length > 0) {
        setUploading(true);
        for (const file of filesToUpload) {
          try {
            const resourceType = file.type.startsWith('image/') ? 'image' : 'raw';
            const sigRes = await axios.get(
              `${API}/cloudinary/signature?resource_type=${resourceType}&folder=uh_communications/jobs/${jobNumber}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            const sig = sigRes.data;

            const formData = new FormData();
            formData.append('file', file);
            formData.append('api_key', sig.api_key);
            formData.append('timestamp', sig.timestamp);
            formData.append('signature', sig.signature);
            formData.append('folder', sig.folder);

            const cloudinaryRes = await axios.post(
              `https://api.cloudinary.com/v1_1/${sig.cloud_name}/${resourceType}/upload`,
              formData
            );

            await axios.post(`${API}/jobs/${createdJobId}/files`, {
              url: cloudinaryRes.data.secure_url,
              public_id: cloudinaryRes.data.public_id,
              filename: file.name,
              resource_type: resourceType,
              format: cloudinaryRes.data.format
            }, { headers: { Authorization: `Bearer ${token}` } });
          } catch (uploadErr) {
            console.error('File upload error:', uploadErr);
          }
        }
        setUploading(false);
      }

      toast.success(`Job ${jobNumber} created!`);
      setDialogOpen(false);
      setNewJob({ title: '', job_type: '', customer_id: '', description: '', budget: '', due_date: '', objectives: '', deliverables: '', notes: '' });
      setFilesToUpload([]);
      setActiveTab('details');
      fetchData();
      
      // Navigate to the new job
      navigate(`/jobs/${createdJobId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create job');
    } finally {
      setCreating(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setFilesToUpload(prev => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setFilesToUpload(prev => prev.filter((_, i) => i !== index));
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      in_progress: 'bg-blue-100 text-blue-800 border-blue-200',
      review: 'bg-purple-100 text-purple-800 border-purple-200',
      completed: 'bg-green-100 text-green-800 border-green-200',
      on_hold: 'bg-gray-100 text-gray-800 border-gray-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200',
      archived: 'bg-slate-100 text-slate-800 border-slate-200'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  // Bulk selection handlers
  const toggleJobSelection = (jobId, e) => {
    e.stopPropagation();
    setSelectedJobs(prev => 
      prev.includes(jobId) 
        ? prev.filter(id => id !== jobId)
        : [...prev, jobId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedJobs.length === filteredJobs.length) {
      setSelectedJobs([]);
    } else {
      setSelectedJobs(filteredJobs.map(job => job.id));
    }
  };

  const handleBulkAction = async () => {
    if (selectedJobs.length === 0) {
      toast.error('No jobs selected');
      return;
    }
    if (bulkAction === 'update_status' && !bulkStatus) {
      toast.error('Please select a status');
      return;
    }

    setProcessingBulk(true);
    try {
      await axios.post(`${API}/jobs/bulk-action`, {
        job_ids: selectedJobs,
        action: bulkAction,
        status: bulkAction === 'update_status' ? bulkStatus : null
      }, { headers: { Authorization: `Bearer ${token}` } });

      const actionMessages = {
        update_status: `Updated status of ${selectedJobs.length} jobs`,
        archive: `Archived ${selectedJobs.length} jobs`,
        delete: `Deleted ${selectedJobs.length} jobs`
      };
      
      toast.success(actionMessages[bulkAction]);
      setBulkActionDialogOpen(false);
      setSelectedJobs([]);
      setBulkAction('');
      setBulkStatus('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk action failed');
    } finally {
      setProcessingBulk(false);
    }
  };

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          job.job_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          job.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1F3C4F]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="jobs-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900">Jobs</h1>
          <p className="text-slate-500 mt-1">Manage your marketing projects</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Bulk Actions */}
          {selectedJobs.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="bulk-actions-btn">
                  <CheckSquare className="w-4 h-4 mr-2" />
                  {selectedJobs.length} Selected
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => { setBulkAction('update_status'); setBulkActionDialogOpen(true); }}>
                  <Filter className="w-4 h-4 mr-2" />
                  Change Status
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { setBulkAction('archive'); setBulkActionDialogOpen(true); }}>
                  <Archive className="w-4 h-4 mr-2" />
                  Archive Selected
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={() => { setBulkAction('delete'); setBulkActionDialogOpen(true); }}
                  className="text-red-600"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Selected
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-[#1F3C4F] hover:bg-[#152a38]"
              data-testid="create-job-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Job
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Create New Job</DialogTitle>
              <DialogDescription>
                Fill in the job details. You can add files and notes now or later.
              </DialogDescription>
            </DialogHeader>
            
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="details">Job Details</TabsTrigger>
                <TabsTrigger value="scope">Scope & Notes</TabsTrigger>
                <TabsTrigger value="files">Files ({filesToUpload.length})</TabsTrigger>
              </TabsList>
              
              <TabsContent value="details" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Job Title *</Label>
                  <Input
                    id="title"
                    placeholder="e.g., Q1 Marketing Campaign, Brand Refresh Project..."
                    value={newJob.title}
                    onChange={(e) => setNewJob({ ...newJob, title: e.target.value })}
                    className="text-base"
                    data-testid="job-title-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Job Type *</Label>
                    <Select 
                      value={newJob.job_type}
                      onValueChange={(value) => setNewJob({ ...newJob, job_type: value })}
                    >
                      <SelectTrigger data-testid="job-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {jobTypes.map((type, index) => (
                          <SelectItem key={type} value={type} data-testid={`job-type-option-${index}`}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Customer *</Label>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm"
                        className="text-[#1F3C4F] h-auto py-0 px-1"
                        onClick={() => setNewCustomerDialogOpen(true)}
                      >
                        <UserPlus className="w-3 h-3 mr-1" />
                        New
                      </Button>
                    </div>
                    <Select 
                      value={newJob.customer_id}
                      onValueChange={(value) => setNewJob({ ...newJob, customer_id: value })}
                    >
                      <SelectTrigger data-testid="job-customer-select">
                        <SelectValue placeholder="Select customer" />
                      </SelectTrigger>
                      <SelectContent>
                        {customers.map((customer) => (
                          <SelectItem key={customer.id} value={customer.id}>
                            {customer.name} {customer.company && `(${customer.company})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="budget">Budget</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="budget"
                        type="number"
                        placeholder="0.00"
                        className="pl-9"
                        value={newJob.budget}
                        onChange={(e) => setNewJob({ ...newJob, budget: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="due_date">Due Date</Label>
                    <Input
                      id="due_date"
                      type="date"
                      value={newJob.due_date}
                      onChange={(e) => setNewJob({ ...newJob, due_date: e.target.value })}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">Project Overview</Label>
                  <Textarea
                    id="description"
                    placeholder="Provide a high-level overview of the project. What is the client looking to achieve? What's the background context?"
                    value={newJob.description}
                    onChange={(e) => setNewJob({ ...newJob, description: e.target.value })}
                    rows={4}
                    className="resize-none"
                  />
                </div>
              </TabsContent>
              
              <TabsContent value="scope" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="objectives">Objectives / Goals</Label>
                  <Textarea
                    id="objectives"
                    placeholder="What are the specific goals for this project?&#10;• Increase brand awareness&#10;• Drive 20% more traffic&#10;• Generate 50 qualified leads"
                    value={newJob.objectives}
                    onChange={(e) => setNewJob({ ...newJob, objectives: e.target.value })}
                    rows={4}
                    className="resize-none"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="deliverables">Deliverables</Label>
                  <Textarea
                    id="deliverables"
                    placeholder="List the specific deliverables for this project:&#10;• Logo design (3 concepts)&#10;• Brand guidelines document&#10;• Social media templates (10)"
                    value={newJob.deliverables}
                    onChange={(e) => setNewJob({ ...newJob, deliverables: e.target.value })}
                    rows={4}
                    className="resize-none"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="notes">Additional Notes</Label>
                  <Textarea
                    id="notes"
                    placeholder="Any other important information, constraints, preferences, or special requirements..."
                    value={newJob.notes}
                    onChange={(e) => setNewJob({ ...newJob, notes: e.target.value })}
                    rows={4}
                    className="resize-none"
                  />
                </div>
              </TabsContent>
              
              <TabsContent value="files" className="space-y-4 mt-4">
                <div 
                  className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-[#1F3C4F] transition-colors cursor-pointer"
                  onClick={() => document.getElementById('file-upload').click()}
                >
                  <Upload className="w-10 h-10 mx-auto text-slate-400" />
                  <p className="mt-2 text-slate-600">Click to select files or drag and drop</p>
                  <p className="text-sm text-slate-400">PDFs, images, documents - reference materials, briefs, assets</p>
                  <input
                    id="file-upload"
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleFileSelect}
                    data-testid="job-file-input"
                  />
                </div>
                
                {filesToUpload.length > 0 && (
                  <div className="space-y-2">
                    <Label>Selected Files ({filesToUpload.length})</Label>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {filesToUpload.map((file, index) => (
                        <div 
                          key={index}
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <File className="w-5 h-5 text-slate-400" />
                            <div>
                              <p className="text-sm font-medium truncate max-w-xs">{file.name}</p>
                              <p className="text-xs text-slate-400">{(file.size / 1024).toFixed(1)} KB</p>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeFile(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleCreateJob} 
                disabled={creating || uploading}
                className="bg-[#1F3C4F] hover:bg-[#152a38]"
                data-testid="job-submit-btn"
              >
                {(creating || uploading) ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {uploading ? 'Uploading files...' : 'Creating...'}
                  </>
                ) : (
                  <>Create Job</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>

      {/* New Customer Dialog */}
      <Dialog open={newCustomerDialogOpen} onOpenChange={setNewCustomerDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Add New Customer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="cust-name">Name *</Label>
              <Input
                id="cust-name"
                placeholder="Contact name"
                value={newCustomer.name}
                onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cust-company">Company</Label>
              <Input
                id="cust-company"
                placeholder="Company name"
                value={newCustomer.company}
                onChange={(e) => setNewCustomer({ ...newCustomer, company: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cust-email">Email</Label>
                <Input
                  id="cust-email"
                  type="email"
                  placeholder="email@company.com"
                  value={newCustomer.email}
                  onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cust-phone">Phone</Label>
                <Input
                  id="cust-phone"
                  placeholder="555-555-5555"
                  value={newCustomer.phone}
                  onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewCustomerDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateCustomer}
              disabled={creatingCustomer}
              className="bg-[#1F3C4F] hover:bg-[#152a38]"
            >
              {creatingCustomer ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Add Customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Action Dialog */}
      <Dialog open={bulkActionDialogOpen} onOpenChange={setBulkActionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {bulkAction === 'update_status' && 'Change Status'}
              {bulkAction === 'archive' && 'Archive Jobs'}
              {bulkAction === 'delete' && 'Delete Jobs'}
            </DialogTitle>
            <DialogDescription>
              This action will affect {selectedJobs.length} selected job{selectedJobs.length !== 1 ? 's' : ''}.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {bulkAction === 'update_status' && (
              <div className="space-y-2">
                <Label>New Status</Label>
                <Select value={bulkStatus} onValueChange={setBulkStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="review">Review</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="on_hold">On Hold</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {bulkAction === 'archive' && (
              <p className="text-slate-600">
                Are you sure you want to archive the selected jobs? Archived jobs will be moved to the "Archived" status.
              </p>
            )}
            {bulkAction === 'delete' && (
              <p className="text-red-600">
                <strong>Warning:</strong> This will permanently delete the selected jobs and all related time entries, expenses, and notes. This action cannot be undone.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkActionDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleBulkAction}
              disabled={processingBulk}
              className={bulkAction === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-[#1F3C4F] hover:bg-[#152a38]'}
            >
              {processingBulk && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {bulkAction === 'update_status' && 'Update Status'}
              {bulkAction === 'archive' && 'Archive Jobs'}
              {bulkAction === 'delete' && 'Delete Jobs'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-center">
        {filteredJobs.length > 0 && (
          <Button 
            variant="outline" 
            size="sm"
            onClick={toggleSelectAll}
            className="shrink-0"
          >
            <Checkbox 
              checked={selectedJobs.length === filteredJobs.length && filteredJobs.length > 0}
              className="mr-2"
            />
            {selectedJobs.length === filteredJobs.length ? 'Deselect All' : 'Select All'}
          </Button>
        )}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search jobs by title, number, or customer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="job-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="review">Review</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="on_hold">On Hold</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Jobs List */}
      {filteredJobs.length > 0 ? (
        <div className="grid gap-4" data-testid="jobs-list">
          {filteredJobs.map((job) => (
            <Card 
              key={job.id} 
              className={`border-0 shadow-sm hover:shadow-md transition-all cursor-pointer ${selectedJobs.includes(job.id) ? 'ring-2 ring-[#1F3C4F]' : ''}`}
              onClick={() => navigate(`/jobs/${job.id}`)}
              data-testid={`job-card-${job.job_number}`}
            >
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div 
                      className="flex items-center justify-center shrink-0"
                      onClick={(e) => toggleJobSelection(job.id, e)}
                    >
                      <Checkbox 
                        checked={selectedJobs.includes(job.id)}
                        className="mr-2"
                      />
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-[#1F3C4F]/10 flex items-center justify-center flex-shrink-0">
                      <Briefcase className="w-6 h-6 text-[#1F3C4F]" />
                    </div>
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-mono text-sm text-[#1F3C4F] font-medium">
                          {job.job_number}
                        </span>
                        <Badge className={getStatusBadge(job.status)}>
                          {job.status?.replace('_', ' ')}
                        </Badge>
                      </div>
                      <h3 className="font-heading font-semibold text-lg text-slate-900">
                        {job.title}
                      </h3>
                      <p className="text-sm text-slate-500 mt-1">
                        {job.customer_name} • {job.job_type}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-6 lg:gap-8">
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-600">{job.total_hours?.toFixed(1) || 0} hrs</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <DollarSign className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-600">{formatCurrency(job.total_expenses)}</span>
                    </div>
                    {job.due_date && (
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        <span className="text-slate-600">
                          {new Date(job.due_date).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-16 text-center">
            <Briefcase className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="font-heading font-semibold text-lg text-slate-700">No jobs found</h3>
            <p className="text-slate-500 mt-1">
              {searchTerm || statusFilter !== 'all' 
                ? 'Try adjusting your search or filters'
                : 'Create your first job to get started'}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default JobsPage;
